/**
 * Centralized session status → Tailwind CSS class mappings.
 * Canonical colors (post WCO-2146):
 *   pending = amber, submitted = purple, received = indigo, ready = cyan,
 *   active = green, paused = yellow, completed = gray, cancelled = red.
 *
 * 'planned' is the legacy DB value (renamed to 'received'). Color mapping
 * retained for backward compatibility with any not-yet-migrated rows.
 */

type StatusColorMap = Record<string, string>;

// ── Badge classes (light background + text, with dark mode) ──────────────
const BADGE_CLASSES: StatusColorMap = {
  pending:   'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  submitted: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  received:  'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
  ready:     'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
  active:    'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  paused:    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  completed: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  // Legacy alias — pre-WCO-2146 'planned' rows render with the new 'received' color
  planned:   'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
};

const DEFAULT_BADGE = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';

// ── Solid-color dot classes (for calendar indicators / status dots) ──────
const DOT_CLASSES: StatusColorMap = {
  pending:   'bg-amber-500',
  submitted: 'bg-purple-500',
  received:  'bg-indigo-500',
  ready:     'bg-cyan-500',
  active:    'bg-green-500',
  paused:    'bg-yellow-500',
  completed: 'bg-gray-400',
  cancelled: 'bg-red-500',
  planned:   'bg-indigo-500',
};

const DEFAULT_DOT = 'bg-gray-400';

// ── Border-left classes (for CalendarView session cards) ─────────────────
const BORDER_CLASSES: StatusColorMap = {
  pending:   'border-l-amber-600',
  submitted: 'border-l-purple-600',
  received:  'border-l-indigo-600',
  ready:     'border-l-cyan-600',
  active:    'border-l-green-600',
  paused:    'border-l-yellow-600',
  completed: 'border-l-gray-500',
  cancelled: 'border-l-red-500',
  planned:   'border-l-indigo-600',
};

const DEFAULT_BORDER = 'border-l-gray-500';

// ── Solid-color with hover (for CalendarView dots) ──────────────────────
const DOT_HOVER_CLASSES: StatusColorMap = {
  pending:   'bg-amber-500 hover:bg-amber-600',
  submitted: 'bg-purple-500 hover:bg-purple-600',
  received:  'bg-indigo-500 hover:bg-indigo-600',
  ready:     'bg-cyan-500 hover:bg-cyan-600',
  active:    'bg-green-500 hover:bg-green-600',
  paused:    'bg-yellow-500 hover:bg-yellow-600',
  completed: 'bg-gray-400 hover:bg-gray-500',
  cancelled: 'bg-red-400 hover:bg-red-500',
  planned:   'bg-indigo-500 hover:bg-indigo-600',
};

const DEFAULT_DOT_HOVER = 'bg-gray-400 hover:bg-gray-500';

/** Normalize status: treat 'in_progress' as 'active' */
function normalize(status: string): string {
  const s = status.toLowerCase().trim();
  return s === 'in_progress' ? 'active' : s;
}

/** Badge classes — light bg + colored text, with dark mode variants */
export function getStatusBadgeClass(status: string): string {
  return BADGE_CLASSES[normalize(status)] ?? DEFAULT_BADGE;
}

/** Solid-color dot class (e.g. bg-purple-500) */
export function getStatusDotClass(status: string): string {
  return DOT_CLASSES[normalize(status)] ?? DEFAULT_DOT;
}

/** Solid-color dot with hover variant (for CalendarView) */
export function getStatusDotHoverClass(status: string): string {
  return DOT_HOVER_CLASSES[normalize(status)] ?? DEFAULT_DOT_HOVER;
}

/** Border-left color class (for CalendarView / ManagerScheduleView cards) */
export function getStatusBorderClass(status: string): string {
  return BORDER_CLASSES[normalize(status)] ?? DEFAULT_BORDER;
}

// ── Station status badge classes (for station management views) ──────────
const STATION_BADGE_CLASSES: StatusColorMap = {
  active:   'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  inactive: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  idle:     'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  paused:   'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  offline:  'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

const DEFAULT_STATION_BADGE = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';

/** Badge classes for station statuses (active, inactive, idle, paused, offline) */
export function getStationStatusBadgeClass(status: string): string {
  return STATION_BADGE_CLASSES[status.toLowerCase().trim()] ?? DEFAULT_STATION_BADGE;
}
